import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { Deck, PagedResult, DeckSearchRequest, DeckMerge } from '../../models';




export interface IDeckService {
	get(id: string): Promise<Deck>;
	getMultiple(ids: string[]): Promise<Deck[]>;
	list(pageIndex: number, pageSize: number): Promise<PagedResult<Deck>>;
	search(request: DeckSearchRequest): Promise<PagedResult<Deck>>;
	save(deck: Deck): Promise<Deck>;
	merge(request: DeckMerge): Promise<Deck>;
	delete(id: string): Promise<any>;

	assignToCompany(deckId: string, companyId: string): Promise<any>;
	assignToArtist(deckId: string, artistId: string): Promise<any>;
	assignToPro(deckId: string, proId: string): Promise<any>;
	assignToSeries(deckId: string, seriesId: string): Promise<any>;
	assignToCategory(deckId: string, categoryId: number): Promise<any>;

	removeFromCompany(deckId: string, companyId: string): Promise<any>;
	removeFromArtist(deckId: string, artistId: string): Promise<any>;
	removeFromPro(deckId: string, proId: string): Promise<any>;
	removeFromSeries(deckId: string, seriesId: string): Promise<any>;
	removeFromCategory(deckId: string, categoryId: number): Promise<any>;

	importFromAos(aosId: string): Promise<Deck>;

}

@injectable()
export class DeckService implements IDeckService {


	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	public get(id: string): Promise<Deck> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${id}`;
		return this.httpClient.get<Deck>(url);
	}

	public getMultiple(ids: string[]): Promise<Deck[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Deck[]>(url);
	}

	public list(pageIndex: number, pageSize: number): Promise<PagedResult<Deck>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Deck>>(url);
	}

	public search(request: DeckSearchRequest): Promise<PagedResult<Deck>> {

		const enabled = request.isEnabled == null
			? null
			: (request.isEnabled.toString() === 'true') ?
				true : false;

		const payload = {
			pageIndex: request.pageIndex,
			pageSize: request.pageSize,
			criteria: request.criteria,
			categoryIds: request.categoryIds,
			seriesIds: request.seriesIds,
			proIds: request.proIds,
			artistIds: request.artistIds,
			companyIds: request.companyIds,
			technologyIds: request.technologyIds,
			isEnabled: enabled,
			year: {
				start: request.startYear,
				end: request.endYear,
			},
			sortBy: request.sortBy,
			sortDirection: request.sortDirection,
		};
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/search`;
		return this.httpClient.post<PagedResult<Deck>>(url, payload);
	}

	public save(deck: Deck): Promise<Deck> {

		const payload: { [k: string]: any } = {
			name: deck.name,
			version: deck.version,
			description: deck.description,
			isApproxYear: deck.isApproxYear,
			isEnabled: deck.isEnabled,
			isReissue: deck.isReissue,
			limitedEdition: deck.limitedEdition,
			guestBoard: deck.guestBoard,
			knownFakes: deck.knownFakes,
			setTotal: deck.setTotal ? parseInt(deck.setTotal.toString(), null) : null,
			setNumber: deck.setNumber ? parseInt(deck.setNumber.toString(), null) : null,
			totalRun: deck.totalRun ? parseInt(deck.totalRun.toString(), null) : null,
			year: deck.year ? parseInt(deck.year.toString(), null) : null,
			width: deck.width ? parseFloat(deck.width.toString()) : null,
			length: deck.length ? parseFloat(deck.length.toString()) : null,
			seasonId: deck.seasonId ? deck.seasonId : 0,
		};

		if (deck.companies && deck.companies.length) { payload.companyIds = deck.companies.map((x) => x.id); }
		if (deck.artists && deck.artists.length) { payload.artistIds = deck.artists.map((x) => x.id); }
		if (deck.pros && deck.pros.length) { payload.proIds = deck.pros.map((x) => x.id); }
		if (deck.categories && deck.categories.length) { payload.categoryIds = deck.categories.map((x) => x.id); }
		if (deck.series && deck.series.length) { payload.seriesIds = deck.series.map((x) => x.id); }
		if (deck.technologies && deck.technologies.length) { payload.technologyIds = deck.technologies.map((x) => x.id); }

		let url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}`;
		if (deck.id !== null && deck.id !== undefined && deck.id !== '') {
			url += `/${deck.id}`;
			return this.httpClient.put(url, payload);
		} else {
			return this.httpClient.post(url, payload);
		}
	}
	public merge(request: DeckMerge): Promise<Deck> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/merge`;
		return this.httpClient.post<Deck>(url, request);
	}

	/** Deletes the specied deck */
	public delete(id: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${id}`;
		return this.httpClient.delete(url);
	}

	/** Assigns the specified deck to the specified category */
	public assignToCategory(deckId: string, categoryId: number): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/categories/${categoryId}`;
		return this.httpClient.post(url, { deckId, categoryId });
	}

	/** Assigns the specified deck to the specified company */
	public assignToCompany(deckId: string, companyId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/companies/${companyId}`;
		return this.httpClient.post(url, { deckId, companyId });
	}

	/** Assigns the specified deck to the specified artist */
	public assignToArtist(deckId: string, artistId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/artists/${artistId}`;
		return this.httpClient.post(url, { deckId, artistId });
	}

	/** Assigns the specified deck to the specified pro */
	public assignToPro(deckId: string, proId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/pros/${proId}`;
		return this.httpClient.post(url, { deckId, proId });
	}

	/** Assigns the specified deck to the specified series */
	public assignToSeries(deckId: string, seriesId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/series/${seriesId}`;
		return this.httpClient.post(url, { deckId, seriesId });
	}

	/** Removes the specified deck from the specified category */
	public removeFromCategory(deckId: string, categoryId: number): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/categories/${categoryId}`;
		return this.httpClient.delete(url, { deckId, categoryId });
	}

	/** Removes the specified deck from the specified company */
	public removeFromCompany(deckId: string, companyId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/companies/${companyId}`;
		return this.httpClient.delete(url, { deckId, companyId });
	}

	/** Removes the specified deck from the specified artist */
	public removeFromArtist(deckId: string, artistId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/artists/${artistId}`;
		return this.httpClient.delete(url, { deckId, artistId });
	}

	/** Removes the specified deck from the specified pro */
	public removeFromPro(deckId: string, proId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/pros/${proId}`;
		return this.httpClient.delete(url, { deckId, proId });
	}

	/** Removes the specified deck from the specified series */
	public removeFromSeries(deckId: string, seriesId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/series/${seriesId}`;
		return this.httpClient.delete(url, { deckId, seriesId });
	}

	public importFromAos(aosId: string): Promise<Deck> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/import/from-aos/${aosId}`;
		return this.httpClient.post<Deck>(url, null);
	}


}
